import { Controller } from "@hotwired/stimulus"

// Mirrors ScoreOption::MAX_SCORE_MAGNITUDE (app/models/score_option.rb) -- keep in sync with the
// model. Score is integer-only for now; no decimal scores until a decimal-safe tally exists.
const MAX_SCORE_INTEGER_DIGITS = 12
const SCORE_FORMAT = /^-?\d+$/

// Matches ScoringOptionsModalComponent#b('row') -- BEM root is the component's UNIQUE_ID.
const ROW_SELECTOR = ".questions--scored--scoring-options-modal-component__row"

export default class extends Controller {
  static targets = [
    "rows",
    "row",
    "rowTemplate",
    "form",
    "saveButton",
    "naCheckbox",
    "naLabel",
    "scoreInput",
    "labelInput"
  ]

  static values = { hasErrors: Boolean }

  declare readonly rowsTarget: HTMLElement
  declare readonly rowTargets: HTMLElement[]
  declare readonly rowTemplateTarget: HTMLTemplateElement
  declare readonly formTarget: HTMLFormElement
  declare readonly saveButtonTarget: HTMLButtonElement
  declare readonly naCheckboxTarget: HTMLInputElement
  declare readonly naLabelTarget: HTMLInputElement
  declare readonly scoreInputTargets: HTMLInputElement[]
  declare readonly labelInputTargets: HTMLInputElement[]
  declare readonly hasErrorsValue: boolean

  private draggedRow: HTMLElement | null = null
  private floatingRow: HTMLElement | null = null
  private pointerOffsetY = 0
  private readonly onPointerMove = (event: PointerEvent): void => this.pointerMove(event)
  private readonly onPointerUp = (): void => this.endDrag()

  // Gated on the server-rendered flag: a re-render after a refused save has lost the classes
  // painted client-side, while a freshly opened modal must not be painted red pre-emptively.
  public connect(): void {
    if (!this.hasErrorsValue) return

    this.paintFormatErrors()
  }

  // Add-only, unlike the toggling used on input/blur/save: the server marks fields it rejected
  // (a duplicate label passes the client format checks), and a toggle here would erase them.
  private paintFormatErrors(): void {
    this.scoreInputTargets.forEach((input) => {
      if (this.scoreErrorMessage(input.value)) input.classList.add("is-invalid")
    })

    this.labelInputTargets.forEach((input) => {
      if (input.value.trim() === "") input.classList.add("is-invalid")
    })
  }

  public disconnect(): void {
    this.endDrag()
  }

  public addOption(): void {
    const fragment = this.rowTemplateTarget.content.cloneNode(true) as DocumentFragment
    this.rowsTarget.appendChild(fragment)
  }

  public deleteOption(event: Event): void {
    const button = event.currentTarget as HTMLElement
    button.closest<HTMLElement>(ROW_SELECTOR)?.remove()
  }

  public dragStart(event: PointerEvent): void {
    if (event.button !== 0) return

    const handle = event.currentTarget as HTMLElement
    const row = handle.closest<HTMLElement>(ROW_SELECTOR)
    if (!row) return

    event.preventDefault() // suppress text selection / native drag while reordering

    this.draggedRow = row
    const rect = row.getBoundingClientRect()
    this.pointerOffsetY = event.clientY - rect.top
    this.floatingRow = this.buildFloatingRow(row, rect)
    document.body.appendChild(this.floatingRow)
    row.classList.add("is-placeholder")

    // Track on document so the drag survives the pointer outrunning the handle.
    document.addEventListener("pointermove", this.onPointerMove)
    document.addEventListener("pointerup", this.onPointerUp)
  }

  private pointerMove(event: PointerEvent): void {
    if (!this.floatingRow || !this.draggedRow) return

    // Vertical-only: `left` stays fixed; `top` clamps to the list's own bounds.
    const bounds = this.rowsTarget.getBoundingClientRect()
    const height = this.floatingRow.offsetHeight
    const top = Math.min(
      Math.max(event.clientY - this.pointerOffsetY, bounds.top),
      bounds.bottom - height,
    )
    this.floatingRow.style.top = `${top}px`

    const sibling = this.rowUnderPointer(event.clientY)
    if (sibling && sibling !== this.draggedRow) {
      const rect = sibling.getBoundingClientRect()
      const isAfter = event.clientY - rect.top > rect.height / 2
      sibling.insertAdjacentElement(isAfter ? "afterend" : "beforebegin", this.draggedRow)
    }
  }

  private endDrag(): void {
    document.removeEventListener("pointermove", this.onPointerMove)
    document.removeEventListener("pointerup", this.onPointerUp)
    this.floatingRow?.remove()
    this.floatingRow = null
    this.draggedRow?.classList.remove("is-placeholder")
    this.draggedRow = null
  }

  // Opaque, viewport-fixed clone matching the grabbed row's geometry. cloneNode
  // skips live input values, so copy them over for a faithful drag image.
  private buildFloatingRow(row: HTMLElement, rect: DOMRect): HTMLElement {
    const clone = row.cloneNode(true) as HTMLElement
    const source = row.querySelectorAll<HTMLInputElement>("input")
    clone.querySelectorAll<HTMLInputElement>("input").forEach((input, index) => {
      input.value = source[index]?.value ?? ""
    })

    clone.classList.add("is-floating")
    clone.style.position = "fixed"
    clone.style.left = `${rect.left}px`
    clone.style.top = `${rect.top}px`
    clone.style.width = `${rect.width}px`
    return clone
  }

  private rowUnderPointer(clientY: number): HTMLElement | null {
    return (
      this.rowTargets.find((row) => {
        if (row === this.draggedRow) return false
        const rect = row.getBoundingClientRect()
        return clientY >= rect.top && clientY <= rect.bottom
      }) ?? null
    )
  }

  public toggleNotApplicable(): void {
    this.naLabelTarget.classList.toggle("hide", !this.naCheckboxTarget.checked)
  }

  public validateScore(event: Event): boolean {
    const input = event.currentTarget as HTMLInputElement
    return this.applyScoreValidation(input)
  }

  private applyScoreValidation(input: HTMLInputElement): boolean {
    const message = this.scoreErrorMessage(input.value)
    input.classList.toggle("is-invalid", !!message)
    return !message
  }

  private scoreErrorMessage(rawValue: string): string | null {
    const value = rawValue.trim()
    if (!value) return "Score is required"
    if (!SCORE_FORMAT.test(value)) return "Score must be a whole number"

    const normalizedDigits = value.replace("-", "").replace(/^0+(?=\d)/, "")
    if (normalizedDigits.length > MAX_SCORE_INTEGER_DIGITS) return "Score is too large"

    return null
  }

  private allScoresValid(): boolean {
    return this.scoreInputTargets.map((input) => this.applyScoreValidation(input)).every(Boolean)
  }

  public validateLabel(event: Event): boolean {
    const input = event.currentTarget as HTMLInputElement
    return this.applyLabelValidation(input)
  }

  private applyLabelValidation(input: HTMLInputElement): boolean {
    const valid = input.value.trim() !== ""
    input.classList.toggle("is-invalid", !valid)
    return valid
  }

  private allLabelsValid(): boolean {
    return this.labelInputTargets.map((input) => this.applyLabelValidation(input)).every(Boolean)
  }

  // Score/label validity is paint-and-proceed, never blocked: only toggles `is-invalid` styling.
  // Duplicate scores are also allowed -- the server (Question::Scored::ScoreOptions::Sync) stays
  // permissive on them.
  public save(): void {
    this.allScoresValid()
    this.allLabelsValid()

    this.formTarget.requestSubmit()
  }
}
